import {
	QueryObserverOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { AxiosError } from 'axios'
import { useMemo } from 'react'
import { OrganizationService } from '../../lib/api'
import { useDebounce } from '../common/useDebounce'
import {
	ChangeOrganizationDataProps,
	CheckOrganizationExist,
	GetCategoriesDataResponse,
	GetGroupsDataResponse,
	GetPermissionSetsDataResponse,
	GetRolesDataResponse,
	GetUsersDataResponse,
	InviteUserDataProps,
	GetOrganizationUsersProps,
	SetDefaultCategoryDataProps,
} from '../../lib/api/types/organization.interfaces'
import { DefaultGetParamsProps } from '../../lib/api/types/interfaces'
import { handleApiError } from '../../lib/utils/handleApiError'
import { UploadAvatarDataProps } from '../../lib/api/types/user.interfaces'
import toastCaller from '../../lib/utils/toastCaller'
import { useAdminFilterUsers } from '../../hooks/table/useColumnFilters'

// organization hooks
export const useCreateOrganization = () => {
	const mutation = useMutation({
		mutationKey: ['organizationCreate'],
		mutationFn: ({ data }: { data: ChangeOrganizationDataProps }) =>
			OrganizationService.create({ data }),
		onError: (error) => {
			handleApiError(error)
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
export const useDeleteOrganization = () => {
	const mutation = useMutation({
		mutationKey: ['organizationDelete'],
		mutationFn: () => OrganizationService.delete(),
		onError: (error) => {
			console.error(error)
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
export const useUpdateOrganization = () => {
	const mutation = useMutation({
		mutationKey: ['organizationUpdate'],
		mutationFn: ({
			data,
			id,
		}: {
			data: ChangeOrganizationDataProps
			id?: string
		}) => OrganizationService.updateProfile({ data, id }),
		onError: (error) => {
			handleApiError(error)
		},
		onSuccess: () => {
			toastCaller({
				type: 'success',
				message: 'Organization profile successfully updated.',
				autoClose: 2000,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
export const useUpdateOrganizationLogo = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['organizationUpdateLogo'],
		mutationFn: ({ data, id }: { data: UploadAvatarDataProps; id?: string }) =>
			OrganizationService.updateLogo({ data, id }),
		onError: (error) => {
			handleApiError(error)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['user'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

// admin user actions
export const useInviteUserToOrganization = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['organizationInviteUser'],
		mutationFn: ({
			data,
			id,
			invalidate,
		}: {
			data: InviteUserDataProps
			id: string
			invalidate?: boolean
		}) =>
			data?.userId
				? OrganizationService.reinviteUser({ data, id })
				: OrganizationService.inviteUser({ data, id }),
		onError: (error, { data }) => {
			const email = Array.isArray(data?.email)
				? data.email[0]
				: data?.email || ''
			handleApiError(error, email)
		},
		onSuccess: (_, { data, invalidate }) => {
			toastCaller({
				type: 'success',
				message: data?.userId
					? 'Invitation resent successfully'
					: 'Invitation sent successfully',
				autoClose: 2000,
			})

			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['organizationUsers'],
					exact: false,
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
export const useInviteMultipleUsersToOrganization = () => {
	const queryClient = useQueryClient()
	const mutation = useMutation({
		mutationKey: ['organizationInviteMultipleUsers'],
		mutationFn: async ({
			email,
			message,
			id,
		}: {
			email: string[]
			message?: string
			id: string
		}) =>
			OrganizationService.inviteUser({
				data: { email, message },
				id,
			}),
		onError: (error, { email }) => {
			const axiosError = error as AxiosError<{
				message?: string
				errors?: string[]
				data?: { Objects?: string[] }
			}>
			const objects = axiosError?.response?.data?.data?.Objects

			if (objects && Array.isArray(objects)) {
				objects.forEach((obj) => {
					const [failedEmail, errorCode] = obj.split(':').map((s) => s.trim())
					if (failedEmail && errorCode) {
						handleApiError(
							{
								...axiosError,
								response: {
									...axiosError.response,
									data: {
										...axiosError.response?.data,
										errors: [errorCode],
									},
								},
							} as AxiosError,
							failedEmail,
						)
					}
				})

				if (objects.length < email.length) {
					queryClient.invalidateQueries({
						queryKey: ['organizationUsers'],
						exact: false,
					})
				}
			} else {
				handleApiError(error)
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['organizationUsers'],
				exact: false,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
export const useDeleteUserFromOrganization = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['organizationDeleteUser'],
		mutationFn: ({
			userId,
			id,
			invalidate,
		}: {
			userId: string
			id: string
			invalidate?: boolean
		}) => OrganizationService.deleteUser({ userId, id }),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: (_, { invalidate = true }) => {
			invalidate &&
				queryClient.invalidateQueries({
					queryKey: ['organizationUsers'],
					exact: false,
				})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

// get admin items hooks
export const useOrganizationRoles = (
	{ query: searchQuery, ...params }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetRolesDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	const debouncedQuery = useDebounce(searchQuery || '', 700)

	return useQuery<GetRolesDataResponse>({
		queryKey: ['organizationRoles', params?.id, debouncedQuery?.trim()],
		queryFn: () =>
			OrganizationService.getRoles({
				...params,
				query: debouncedQuery?.trim(),
			}),
		enabled: isAuthenticated && Boolean(params?.id),
		...options,
	})
}
export const useOrganizationUsers = (
	{ id, filtering, query: searchQuery }: GetOrganizationUsersProps,
	options?: Partial<QueryObserverOptions<GetUsersDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	const debouncedQuery = useDebounce(searchQuery || '', 700)

	const query = useQuery({
		queryKey: ['organizationUsers', id, debouncedQuery?.trim()],
		queryFn: () =>
			OrganizationService.getUsers({
				id,
				query: debouncedQuery?.trim(),
			}),
		enabled: isAuthenticated && Boolean(id),
		...options,
	})

	const filteredData = useMemo(() => {
		if (!query.data?.data?.entries || !filtering || filtering === 'all') {
			return query.data
		}

		const filteredEntries = query.data.data.entries.filter((user: any) =>
			useAdminFilterUsers(user, filtering),
		)

		return {
			...query.data,
			data: {
				...query.data.data,
				entries: filteredEntries,
				pagination: {
					...query.data.data.pagination,
					totalCount: filteredEntries.length,
				},
			},
		}
	}, [query.data, filtering])

	return {
		...query,
		data: filteredData,
	}
}
export const useOrganizationGroups = (
	{ id, query = '', offset = 0, limit = 1000 }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetGroupsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	const debouncedQuery = useDebounce(query || '', 700)

	return useQuery({
		queryKey: ['organizationGroups', id, debouncedQuery?.trim()],
		queryFn: () =>
			OrganizationService.getGroups({
				id,
				query: debouncedQuery?.trim(),
				offset,
				limit: limit || 1000,
			}),
		enabled: isAuthenticated && Boolean(id),
		...options,
	})
}
export const useOrganizationPermissionSets = (
	{ id, query: searchQuery }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetPermissionSetsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	const debouncedQuery = useDebounce(searchQuery || '', 700)

	return useQuery<GetPermissionSetsDataResponse>({
		queryKey: ['organizationPermissionSets', id, debouncedQuery?.trim()],
		queryFn: () =>
			OrganizationService.getPermissionSets({
				id,
				query: debouncedQuery?.trim(),
			}),
		enabled: isAuthenticated && Boolean(id),
		...options,
	})
}
export const useOrganizationCategories = (
	{ id, query: searchQuery }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetCategoriesDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	const debouncedQuery = useDebounce(searchQuery || '', 700)

	return useQuery({
		queryKey: ['organizationCategories', id, debouncedQuery?.trim()],
		queryFn: () =>
			OrganizationService.getCategories({
				id,
				query: debouncedQuery?.trim(),
			}),
		enabled: isAuthenticated && Boolean(id),
		...options,
	})
}

// change admin items hooks
export const useSetDefaultCategory = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['setDefaultCategory'],
		mutationFn: ({ id, parentId }: SetDefaultCategoryDataProps) =>
			OrganizationService.setDefaultCategory({ id, parentId }),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['organizationCategories'],
				exact: false,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
export const useToggleUserStatus = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['organizationToggleUserStatus'],
		mutationFn: ({
			value,
			userId,
			id,
			invalidate = true,
		}: {
			value: boolean
			userId: string
			id: string
			invalidate?: boolean
		}) => OrganizationService.toggleUserStatus({ value, userId, id }),
		onError: (error) => {
			handleApiError(error)
		},
		onSuccess: (_, { invalidate }) => {
			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['organizationUsers'],
					exact: false,
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
export const useToggleUserAdminRights = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['organizationToggleUserAdminRights'],
		mutationFn: ({
			value,
			userId,
			id,
			invalidate = true,
		}: {
			value: boolean
			userId: string
			id: string
			invalidate?: boolean
		}) => OrganizationService.toggleUserAdminRights({ value, userId, id }),
		onError: (error) => {
			console.error(error)
			handleApiError(error)
		},
		onSuccess: (_, { invalidate }) => {
			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['organizationUsers'],
					exact: false,
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
export const useCheckOrganizationExist = (
	params: CheckOrganizationExist,
	options?: Partial<QueryObserverOptions<unknown>>, // TODO: set response interface
) => {
	const { isAuthenticated } = useAuth()
	return useQuery({
		queryKey: ['organizationExist', params?.slug],
		queryFn: () => OrganizationService.checkOrganizationExist(params),
		enabled: isAuthenticated && Boolean(params?.slug),
		...options,
	})
}
export const useCheckOrganizationSlug = () => {
	return useMutation({
		mutationKey: ['organizationCheckSlug'],
		mutationFn: ({ slug }: { slug: string }) =>
			OrganizationService.checkOrganizationSlug({ slug }),
		onError: (error) => {
			handleApiError(error)
			// Don't show toast for slug check errors, handle in component
			console.error('Error checking slug:', error)
		},
	})
}
